import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";

import {
  Box,
  Button,
  HStack,
  HTMLWrapper,
  IconButton,
  Tag,
  CloseIcon,
  Flex,
  Heading,
  useBreakpointValue,
  FiShare2,
  SocialShare,
  VStack
} from "@/storybook-components";
import { useAppSelector } from "@/redux/store/hooks";

const LeftNode = ({
  isOpen,
  toggleSidebar,
  isDescriptionLong,
  name,
  description,
  tagList
}) => {
  const { query } = useRouter();
  const { projectId, brainstormerId } = query;
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const { settings } = useAppSelector((state) => state.settings);
  const { translate } = useTranslator();
  const isViewFeedButtonVisible = useBreakpointValue(
    {
      base: true,
      lg: false
    },
    "false"
  );

  /**
   * Left Sliding Panel content
   */
  return (
    <Flex
      className="ehq-ideas-about"
      direction="column"
      px={4}
      py={6}
      gap={8}
      bg="gray.100"
      minH="100svh"
    >
      <Box w="full" className="ehq-ideas-about-header">
        <HStack as="section" justifyContent="space-between">
          <Heading
            as="h2"
            fontSize="md"
            fontWeight="semibold"
            lineHeight="shorter"
          >
            {translate("brainstormers.about")}
          </Heading>
          <IconButton
            aria-label={translate("brainstormers.closePanel")}
            icon={<CloseIcon />}
            size="xs"
            variant="ghost"
            color="gray.900"
            _hover={{
              background: "gray.100"
            }}
            tabIndex={isOpen ? -1 : 0}
            onClick={toggleSidebar}
          />
        </HStack>
      </Box>

      {name && (
        <Flex
          direction="column"
          align="flex-start"
          gap={6}
          pb={isViewFeedButtonVisible ? 0 : 6}
          flex={1}
          minH={isViewFeedButtonVisible ? 0 : "95vh"}
          className="ehq-ideas-about-content"
        >
          {/* Title */}
          <Heading
            as="h1"
            fontWeight="bold"
            fontSize="2xl"
            color="gray.900"
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
          >
            {name}
          </Heading>

          {/* Description */}
          <HTMLWrapper
            isFullWidth
            fontSize="sm"
            color="gray.800"
            html={description}
          />

          {/* Footer */}
          <Flex
            direction="column"
            grow={1}
            justify="space-between"
            h={isDescriptionLong ? null : ["60vh", "62vh", "1vh"]}
            gap={6}
            minW="full"
            className="ehq-ideas-about-footer"
            mt={2}
          >
            <VStack align="flex-start" gap={4}>
              {tagList.length && (
                <HStack
                  wrap="wrap"
                  gap={{ base: 4, xl: 2 }}
                  alignItems="flex-start"
                >
                  {tagList &&
                    tagList.map((tag, index) => (
                      <Tag
                        colorScheme="whiteAlpha"
                        variant="solid"
                        color="gray.800"
                        fontSize="sm"
                        backgroundColor="white"
                        key={index}
                      >
                        {tag}
                      </Tag>
                    ))}
                </HStack>
              )}
              {settings.disableSocialMediaSharing ? null : (
                <Box sx={{ mt: "0 !important" }}>
                  <SocialShare
                    title={name}
                    placement="auto"
                    shareUrl={`${baseUrl}/${projectId}/brainstormers/${brainstormerId}`}
                    trigger={
                      <Button
                        px={3}
                        size="lg"
                        variant="tertiary"
                        leftIcon={<FiShare2 />}
                        backgroundColor="transparent"
                      >
                        {translate("common.share")}
                      </Button>
                    }
                  />
                </Box>
              )}
            </VStack>

            {isViewFeedButtonVisible && (
              <HStack alignItems="flex-end">
                <Button
                  variant="primary"
                  size="lg"
                  w="full"
                  onClick={toggleSidebar}
                >
                  {translate("brainstormers.viewFeed")}
                </Button>
              </HStack>
            )}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default LeftNode;
